import * as Dropdown from "@radix-ui/react-dropdown-menu";
import * as Sentry from "@sentry/react";
import { Building2, Check, ChevronRight } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";

/**
 * PRTL-1348: multi-account switcher, rendered as a "Switch Organization"
 * entry inside the profile (avatar) dropdown in the header. It must be
 * mounted inside that menu's Dropdown.Root — it renders a Radix submenu
 * that flies out the org list. Shown only when the multiOrgAccounts flag
 * is on and the user belongs to two or more organizations.
 *
 * Switching mints a token scoped to the chosen org (the previous session
 * is revoked server-side) and hard-resets the app so no cached data from
 * the previous org survives.
 */
export const OrgSwitcherMenuItem = () => {
    const flags = useFeatureFlags();
    const { data: user } = trpc.users.me.useQuery(undefined, {
        enabled: flags.multiOrgAccounts,
    });
    const utils = trpc.useUtils();
    const switchMutation = trpc.auth.switchOrganization.useMutation({
        onSuccess: ({ token }) => {
            tokenManager.set(token);
            window.location.assign("/");
        },
        onError: (e) => {
            // Membership may have been revoked since users.me loaded — the
            // server re-checks. Surface it and refresh the membership list.
            Sentry.captureException(e, { extra: { action: "switchOrganization" } });
            enqueueSnackbar(e.message || "Could not switch organizations. Please try again.", {
                variant: "error",
            });
            void utils.users.me.invalidate();
        },
    });

    if (!flags.multiOrgAccounts) return null;
    const memberships = user?.memberships ?? [];
    if (memberships.length < 2) return null;

    return (
        <li className="rounded-full bg-[#eeeeee] p-2 px-4">
            <Dropdown.Sub>
                <Dropdown.SubTrigger
                    disabled={switchMutation.isPending}
                    className="block w-full cursor-pointer font-sans text-[17px] font-normal leading-normal text-[#424242] outline-none focus:outline-none focus:ring-0"
                >
                    <div className="flex flex-row items-center space-x-2">
                        <div>
                            <Building2 className="h-[24px] w-[24px]" />
                        </div>
                        <div>Switch Organization</div>
                        <ChevronRight className="!ml-auto h-4 w-4 text-gray-500" />
                    </div>
                </Dropdown.SubTrigger>
                <Dropdown.Portal>
                    <Dropdown.SubContent
                        sideOffset={16}
                        className="z-50 w-[260px] rounded-2xl border border-[#d4d5d5] bg-white p-2 shadow-lg"
                    >
                        <Dropdown.Label className="px-2 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                            Organizations
                        </Dropdown.Label>
                        {memberships.map((m) => {
                            const isActive = m.organizationId === user?.activeOrganizationId;
                            return (
                                <Dropdown.Item
                                    key={m.organizationId}
                                    disabled={switchMutation.isPending}
                                    onSelect={(e) => {
                                        if (isActive) return;
                                        e.preventDefault();
                                        switchMutation.mutate({
                                            organizationId: m.organizationId,
                                        });
                                    }}
                                    className="radix-highlighted:bg-gray-100 flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-[#424242] outline-none"
                                >
                                    <span className="bg-brand-atlantic flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold text-white">
                                        {m.organizationName.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="flex-1 truncate font-medium">
                                        {m.organizationName}
                                    </span>
                                    {isActive && <Check className="h-4 w-4 text-[#0F8EA6]" />}
                                    {switchMutation.isPending &&
                                        switchMutation.variables?.organizationId ===
                                            m.organizationId && (
                                            <span className="text-xs text-gray-500">
                                                Switching…
                                            </span>
                                        )}
                                </Dropdown.Item>
                            );
                        })}
                    </Dropdown.SubContent>
                </Dropdown.Portal>
            </Dropdown.Sub>
        </li>
    );
};
